import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent } from "@mui/material";

import { useGetTaxRates } from "../../../hooks/tax/useGetTaxRates";
import { useGetTaxZones } from "../../../hooks/tax/useGetTaxZones";
import { useDeleteTaxRate } from "../../../hooks/tax/useDeleteTaxRate";
import { useUpdateTaxRate } from "../../../hooks/tax/useUpdateTaxRate";
import useTableActions from "../../../hooks/common/useTableActions";

import TableLayout from "../../global/Table/TableLayout";
import { TaxRatesTableColumns } from "./Table/TaxRatesTableColumns";
import TaxRatesTableRow from "./Table/TaxRatesTableRow";
import FormAddEditTaxRate from "./FormAddEditTaxRate";

const TaxRatesTable = () => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || "en").startsWith("ar");

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [zoneFilter, setZoneFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    const { deleteTaxRateMutate, isDeleting } = useDeleteTaxRate();
    const { updateTaxRateMutate } = useUpdateTaxRate();
    const { zones } = useGetTaxZones({ per_page: 200 });

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        selectedItem,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleMenuDelete,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/tax",
        deleteSingle: deleteTaxRateMutate,
    });

    const params = useMemo(() => ({
        page,
        per_page: perPage,
        search: search || undefined,
        zone_id: zoneFilter || undefined,
        is_active: statusFilter === "" ? undefined : statusFilter,
        sort_by: "created_at",
        sort_order: order,
    }), [page, perPage, search, order, zoneFilter, statusFilter]);

    const { rates, meta, isLoading, error, refetch } = useGetTaxRates(params);

    const zoneOptions = useMemo(() => ([
        { value: "", label: { ar: "الكل", en: "All Zones" } },
        ...zones.map((z) => {
            const lng = i18n.language || "en";
            const label = z.name?.[lng] || z.name?.en || z.name?.ar || z.name || "-";
            return { value: String(z.id), label: { ar: label, en: label } };
        }),
    ]), [zones, i18n.language]);

    const tableFilters = [
        {
            name: "zone",
            label: { ar: "المنطقة", en: "Zone" },
            value: zoneFilter,
            onChange: setZoneFilter,
            options: zoneOptions,
        },
        {
            name: "status",
            label: { ar: "الحالة", en: "Status" },
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
                { value: "", label: { ar: "الكل", en: "All" } },
                { value: "1", label: { ar: "نشط", en: "Active" } },
                { value: "0", label: { ar: "غير نشط", en: "Inactive" } },
            ],
        },
    ];

    const openCreate = () => {
        setEditingItem(null);
        setDialogOpen(true);
    };

    const openEdit = () => {
        if (!selectedItem) return;
        setEditingItem(selectedItem);
        setDialogOpen(true);
        handleMenuClose();
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setEditingItem(null);
    };

    const handleToggleActive = (item) => {
        updateTaxRateMutate({
            id: item.id,
            data: { is_active: !item.is_active },
        });
    };

    const renderRow = (rowParams) => (
        <TaxRatesTableRow
            key={rowParams.item.id}
            item={rowParams.item}
            index={rowParams.index}
            isSelected={selectedIds.includes(rowParams.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            onToggleActive={handleToggleActive}
        />
    );

    return (
        <>
            <TableLayout
                title={isArabic ? "النسب الضريبية" : "Tax Rates"}
                subtitle={isArabic ? "إدارة نسب الضرائب لكل منطقة" : "Manage tax rates per zone"}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={isArabic ? "إضافة نسبة" : "Add Rate"}
                onAddClick={openCreate}
                isLoading={isLoading}
                error={error}
                items={rates}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                order={order}
                onRequestSort={handleRequestSort}
                columns={TaxRatesTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuEdit={openEdit}
                onMenuDelete={handleMenuDelete}
                isDeletingSingle={isDeleting}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, rates)}
                onRowCheckboxClick={handleRowCheckboxClick}
                bulkDeleteDisabled
                filters={tableFilters}
                onClearAllFilters={() => {
                    setZoneFilter("");
                    setStatusFilter("");
                }}
                emptyLabelAr="لا توجد نسب ضريبية"
                emptyLabelEn="No tax rates"
                refetch={refetch}
            />

            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
                <DialogContent sx={{ p: { xs: 2, md: 4 } }}>
                    <FormAddEditTaxRate
                        isEdit={!!editingItem}
                        currentData={editingItem}
                        onSubmitSuccess={closeDialog}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default TaxRatesTable;
